// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  tablesHeading: 'Tables this app keeps',
  liveNote: 'Live data, the same for every version',
  tableMeta: '{count} entries · last change {at} by {who}',
  tableMetaNoChange: '{count} entries',
  filesMeta: '{count} files · {size}',
  fields: '{count} fields',
  fieldsNoun: 'one:field|other:fields',
  exportCsv: 'Export CSV',
  moreActions: 'More actions',
  viewFiles: 'View',
  restoreToTime: 'Restore to a time…',
  rowHistory: 'Row history',
  rowHistoryTitle: 'History of this row',
  rowHistoryEmpty: 'No changes recorded for this row.',
  rowHistoryCreated: 'Added by {who}',
  rowHistoryChanged: '{field}: {from} → {to}',
  rowHistoryImageChanged: '{field} changed',
  rowHistoryBy: '{who}, {at}',
  undo: 'Undo',
  undoBusy: 'Undoing…',
  undoUnavailable: 'Can’t be undone',
  absent: '—',
  loadMore: 'Show more',
  loading: 'Loading entries…',
  loadFailed: 'The entries couldn’t be loaded. Try again.',
  rowsFailed: 'The entries couldn’t be loaded.',
  retry: 'Try again',
  noEntries: 'No entries yet.',
  searchLabel: 'Search entries',
  sourcesHeading: 'Sources it reads',
  sourcesNone: 'Reads no company systems. Its data is its own.',
  sourceReadOnly: 'Read only',
  sourceReadWrite: 'Reads and proposes writes',
  sourceAllowed: 'Allowed by {who}, {at}',
  sourceLastRead: 'Last read {at}',
  sourceNeverRead: 'Not read yet',
  sourceViaOwner: 'Through {owner}’s connection',
  sourceViaViewer: 'Through each person’s own connection',
  manage: 'Manage',
  keptHeading: 'Kept history',
  keptSince: '{name} · since {since}',
  keptNote: 'Survives Clear all entries unless the owner ticks the box.',
  pendingHeading: 'Waiting for your OK',
  pendingNote: 'These changes would remove data. Nothing is removed until you approve.',
  pendingReview: 'Review',
  clearEntries: 'Clear all entries…',
  recovering: 'Recovering',
};

export type AppDataCopyKey = keyof typeof en;

export const appDataCopy: NamespaceCatalog<keyof typeof en> = {
  en: {
    tablesHeading: 'Tables this app keeps',
    liveNote: 'Live data, the same for every version',
    tableMeta: '{count} entries · last change {at} by {who}',
    tableMetaNoChange: '{count} entries',
    filesMeta: '{count} files · {size}',
    fields: '{count} fields',
    fieldsNoun: 'one:field|other:fields',
    exportCsv: 'Export CSV',
    moreActions: 'More actions',
    viewFiles: 'View',
    restoreToTime: 'Restore to a time…',
    rowHistory: 'Row history',
    rowHistoryTitle: 'History of this row',
    rowHistoryEmpty: 'No changes recorded for this row.',
    rowHistoryCreated: 'Added by {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} changed',
    rowHistoryBy: '{who}, {at}',
    undo: 'Undo',
    undoBusy: 'Undoing…',
    undoUnavailable: 'Can’t be undone',
    absent: '—',
    loadMore: 'Show more',
    loading: 'Loading entries…',
    loadFailed: 'The entries couldn’t be loaded. Try again.',
    rowsFailed: 'The entries couldn’t be loaded.',
    retry: 'Try again',
    noEntries: 'No entries yet.',
    searchLabel: 'Search entries',
    sourcesHeading: 'Sources it reads',
    sourcesNone: 'Reads no company systems. Its data is its own.',
    sourceReadOnly: 'Read only',
    sourceReadWrite: 'Reads and proposes writes',
    sourceAllowed: 'Allowed by {who}, {at}',
    sourceLastRead: 'Last read {at}',
    sourceNeverRead: 'Not read yet',
    sourceViaOwner: 'Through {owner}’s connection',
    sourceViaViewer: 'Through each person’s own connection',
    manage: 'Manage',
    keptHeading: 'Kept history',
    keptSince: '{name} · since {since}',
    keptNote: 'Survives Clear all entries unless the owner ticks the box.',
    pendingHeading: 'Waiting for your OK',
    pendingNote: 'These changes would remove data. Nothing is removed until you approve.',
    pendingReview: 'Review',
    clearEntries: 'Clear all entries…',
    recovering: 'Recovering',
  },
  bg: {
    tablesHeading: 'Таблици, които това приложение поддържа',
    liveNote: 'Актуални данни, еднакви за всички версии',
    tableMeta: '{count} записа · последна промяна {at} от {who}',
    tableMetaNoChange: '{count} записа',
    filesMeta: '{count} файла · {size}',
    fields: '{count} полета',
    fieldsNoun: 'one:поле|other:полета',
    exportCsv: 'Експортиране на CSV',
    moreActions: 'Още действия',
    viewFiles: 'Преглед',
    restoreToTime: 'Възстановяване към момент…',
    rowHistory: 'История на реда',
    rowHistoryTitle: 'История на този ред',
    rowHistoryEmpty: 'Няма записани промени за този ред.',
    rowHistoryCreated: 'Добавено от {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} е променено',
    rowHistoryBy: '{who}, {at}',
    undo: 'Отмени',
    undoBusy: 'Отменяне…',
    undoUnavailable: 'Не може да бъде отменено',
    absent: '—',
    loadMore: 'Покажи още',
    loading: 'Зареждане на записи…',
    loadFailed: 'Записите не можаха да бъдат заредени. Опитайте отново.',
    rowsFailed: 'Записите не можаха да бъдат заредени.',
    retry: 'Опитай отново',
    noEntries: 'Все още няма записи.',
    searchLabel: 'Търсене на записи',
    sourcesHeading: 'Източници, които чете',
    sourcesNone: 'Не чете от фирмени системи. Данните му са собствени.',
    sourceReadOnly: 'Само за четене',
    sourceReadWrite: 'Чете и предлага промени',
    sourceAllowed: 'Разрешено от {who}, {at}',
    sourceLastRead: 'Последно прочетено {at}',
    sourceNeverRead: 'Все още не е прочетено',
    sourceViaOwner: 'Чрез връзката на {owner}',
    sourceViaViewer: 'Чрез собствената връзка на всеки човек',
    manage: 'Управление',
    keptHeading: 'Запазена история',
    keptSince: '{name} · от {since}',
    keptNote:
      'Запазва се при изчистване на всички записи, освен ако собственикът не постави отметка.',
    pendingHeading: 'Изчаква вашето одобрение',
    pendingNote:
      'Тези промени ще премахнат данни. Нищо няма да бъде премахнато, докато не ги одобрите.',
    pendingReview: 'Преглед',
    clearEntries: 'Изчистване на всички записи…',
    recovering: 'Възстановяване',
  },
  cs: {
    tablesHeading: 'Tabulky, které tato aplikace spravuje',
    liveNote: 'Živá data, stejná pro každou verzi',
    tableMeta: '{count} záznamů · poslední změna {at} od {who}',
    tableMetaNoChange: '{count} záznamů',
    filesMeta: '{count} souborů · {size}',
    fields: '{count} polí',
    fieldsNoun: 'one:pole|other:polí',
    exportCsv: 'Exportovat CSV',
    moreActions: 'Další akce',
    viewFiles: 'Zobrazit',
    restoreToTime: 'Obnovit k určitému času…',
    rowHistory: 'Historie řádku',
    rowHistoryTitle: 'Historie tohoto řádku',
    rowHistoryEmpty: 'U tohoto řádku nejsou zaznamenány žádné změny.',
    rowHistoryCreated: 'Přidal(a) {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} změněno',
    rowHistoryBy: '{who}, {at}',
    undo: 'Vrátit zpět',
    undoBusy: 'Vracení změny…',
    undoUnavailable: 'Nelze vrátit zpět',
    absent: '—',
    loadMore: 'Zobrazit další',
    loading: 'Načítají se záznamy…',
    loadFailed: 'Záznamy se nepodařilo načíst. Zkuste to znovu.',
    rowsFailed: 'Záznamy se nepodařilo načíst.',
    retry: 'Zkusit znovu',
    noEntries: 'Zatím žádné záznamy.',
    searchLabel: 'Hledat záznamy',
    sourcesHeading: 'Zdroje, ze kterých čte',
    sourcesNone: 'Nečte z žádných firemních systémů. Její data jsou vlastní.',
    sourceReadOnly: 'Pouze ke čtení',
    sourceReadWrite: 'Čte a navrhuje zápisy',
    sourceAllowed: 'Povolil(a) {who}, {at}',
    sourceLastRead: 'Naposledy načteno {at}',
    sourceNeverRead: 'Zatím nebylo přečteno',
    sourceViaOwner: 'Prostřednictvím připojení vlastníka {owner}',
    sourceViaViewer: 'Prostřednictvím vlastního připojení každé osoby',
    manage: 'Spravovat',
    keptHeading: 'Uchovaná historie',
    keptSince: '{name} · od {since}',
    keptNote: 'Zůstane zachována i po vymazání všech záznamů, dokud vlastník nezaškrtne políčko.',
    pendingHeading: 'Čeká na vaše schválení',
    pendingNote: 'Tyto změny by odstranily data. Nic nebude odstraněno, dokud je neschválíte.',
    pendingReview: 'Zkontrolovat',
    clearEntries: 'Vymazat všechny záznamy…',
    recovering: 'Obnovování',
  },
  da: {
    tablesHeading: 'Tabeller, som denne app vedligeholder',
    liveNote: 'Live data, ens for alle versioner',
    tableMeta: '{count} poster · senest ændret {at} af {who}',
    tableMetaNoChange: '{count} poster',
    filesMeta: '{count} filer · {size}',
    fields: '{count} felter',
    fieldsNoun: 'one:felt|other:felter',
    exportCsv: 'Eksportér CSV',
    moreActions: 'Flere handlinger',
    viewFiles: 'Vis',
    restoreToTime: 'Gendan til et tidspunkt…',
    rowHistory: 'Rækkehistorik',
    rowHistoryTitle: 'Historik for denne række',
    rowHistoryEmpty: 'Ingen ændringer er registreret for denne række.',
    rowHistoryCreated: 'Tilføjet af {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} ændret',
    rowHistoryBy: '{who}, {at}',
    undo: 'Fortryd',
    undoBusy: 'Fortryder…',
    undoUnavailable: 'Kan ikke fortrydes',
    absent: '—',
    loadMore: 'Vis mere',
    loading: 'Indlæser poster…',
    loadFailed: 'Poster kunne ikke indlæses. Prøv igen.',
    rowsFailed: 'Poster kunne ikke indlæses.',
    retry: 'Prøv igen',
    noEntries: 'Ingen poster endnu.',
    searchLabel: 'Søg i poster',
    sourcesHeading: 'Kilder, den læser fra',
    sourcesNone: 'Læser ikke fra virksomhedens systemer. Dens data er dens egne.',
    sourceReadOnly: 'Kun læsning',
    sourceReadWrite: 'Læser og foreslår skrivninger',
    sourceAllowed: 'Tilladt af {who}, {at}',
    sourceLastRead: 'Senest læst {at}',
    sourceNeverRead: 'Ikke læst endnu',
    sourceViaOwner: 'Via {owner}s forbindelse',
    sourceViaViewer: 'Via hver persons egen forbindelse',
    manage: 'Administrer',
    keptHeading: 'Bevaret historik',
    keptSince: '{name} · siden {since}',
    keptNote: 'Bevares efter Ryd alle poster, medmindre ejeren markerer feltet.',
    pendingHeading: 'Afventer din godkendelse',
    pendingNote: 'Disse ændringer vil fjerne data. Intet fjernes, før du godkender.',
    pendingReview: 'Gennemgå',
    clearEntries: 'Ryd alle poster…',
    recovering: 'Gendanner',
  },
  de: {
    tablesHeading: 'Von dieser App verwaltete Tabellen',
    liveNote: 'Live-Daten, für jede Version gleich',
    tableMeta: '{count} Einträge · letzte Änderung {at} von {who}',
    tableMetaNoChange: '{count} Einträge',
    filesMeta: '{count} Dateien · {size}',
    fields: '{count} Felder',
    fieldsNoun: 'one:Feld|other:Felder',
    exportCsv: 'CSV exportieren',
    moreActions: 'Weitere Aktionen',
    viewFiles: 'Anzeigen',
    restoreToTime: 'Auf einen Zeitpunkt zurücksetzen…',
    rowHistory: 'Zeilenverlauf',
    rowHistoryTitle: 'Verlauf dieser Zeile',
    rowHistoryEmpty: 'Für diese Zeile sind keine Änderungen erfasst.',
    rowHistoryCreated: 'Hinzugefügt von {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} geändert',
    rowHistoryBy: '{who}, {at}',
    undo: 'Rückgängig',
    undoBusy: 'Wird rückgängig gemacht…',
    undoUnavailable: 'Kann nicht rückgängig gemacht werden',
    absent: '—',
    loadMore: 'Mehr anzeigen',
    loading: 'Einträge werden geladen…',
    loadFailed: 'Die Einträge konnten nicht geladen werden. Versuchen Sie es erneut.',
    rowsFailed: 'Die Einträge konnten nicht geladen werden.',
    retry: 'Erneut versuchen',
    noEntries: 'Noch keine Einträge.',
    searchLabel: 'Einträge durchsuchen',
    sourcesHeading: 'Von dieser App gelesene Quellen',
    sourcesNone: 'Liest keine Unternehmenssysteme. Die Daten stammen aus der App selbst.',
    sourceReadOnly: 'Nur Lesen',
    sourceReadWrite: 'Liest Daten und schlägt Änderungen vor',
    sourceAllowed: 'Erlaubt von {who}, {at}',
    sourceLastRead: 'Zuletzt gelesen {at}',
    sourceNeverRead: 'Noch nicht gelesen',
    sourceViaOwner: 'Über die Verbindung von {owner}',
    sourceViaViewer: 'Über die eigene Verbindung jeder Person',
    manage: 'Verwalten',
    keptHeading: 'Beibehaltene Chronik',
    keptSince: '{name} · seit {since}',
    keptNote:
      'Bleibt erhalten, wenn alle Einträge gelöscht werden, außer der Eigentümer aktiviert das Kontrollkästchen.',
    pendingHeading: 'Wartet auf Ihre Bestätigung',
    pendingNote:
      'Diese Änderungen würden Daten entfernen. Es wird nichts entfernt, bevor Sie zustimmen.',
    pendingReview: 'Überprüfen',
    clearEntries: 'Alle Einträge löschen…',
    recovering: 'Wird wiederhergestellt',
  },
  el: {
    tablesHeading: 'Πίνακες που διατηρεί αυτή η εφαρμογή',
    liveNote: 'Ζωντανά δεδομένα, ίδια για κάθε έκδοση',
    tableMeta: '{count} εγγραφές · τελευταία αλλαγή {at} από {who}',
    tableMetaNoChange: '{count} εγγραφές',
    filesMeta: '{count} αρχεία · {size}',
    fields: '{count} πεδία',
    fieldsNoun: 'one:πεδίο|other:πεδία',
    exportCsv: 'Εξαγωγή CSV',
    moreActions: 'Περισσότερες ενέργειες',
    viewFiles: 'Προβολή',
    restoreToTime: 'Επαναφορά σε χρονική στιγμή…',
    rowHistory: 'Ιστορικό γραμμής',
    rowHistoryTitle: 'Ιστορικό αυτής της γραμμής',
    rowHistoryEmpty: 'Δεν καταγράφηκαν αλλαγές για αυτήν τη γραμμή.',
    rowHistoryCreated: 'Προστέθηκε από {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: 'Το {field} άλλαξε',
    rowHistoryBy: '{who}, {at}',
    undo: 'Αναίρεση',
    undoBusy: 'Αναίρεση…',
    undoUnavailable: 'Δεν είναι δυνατή η αναίρεση',
    absent: '—',
    loadMore: 'Εμφάνιση περισσότερων',
    loading: 'Φόρτωση εγγραφών…',
    loadFailed: 'Δεν ήταν δυνατή η φόρτωση των εγγραφών. Δοκιμάστε ξανά.',
    rowsFailed: 'Δεν ήταν δυνατή η φόρτωση των εγγραφών.',
    retry: 'Δοκιμή ξανά',
    noEntries: 'Δεν υπάρχουν ακόμη εγγραφές.',
    searchLabel: 'Αναζήτηση εγγραφών',
    sourcesHeading: 'Πηγές από τις οποίες διαβάζει',
    sourcesNone: 'Δεν διαβάζει από εταιρικά συστήματα. Τα δεδομένα της είναι δικά της.',
    sourceReadOnly: 'Μόνο για ανάγνωση',
    sourceReadWrite: 'Διαβάζει και προτείνει εγγραφές',
    sourceAllowed: 'Επιτρέπεται από {who}, {at}',
    sourceLastRead: 'Τελευταία ανάγνωση {at}',
    sourceNeverRead: 'Δεν έχει διαβαστεί ακόμη',
    sourceViaOwner: 'Μέσω της σύνδεσης του/της {owner}',
    sourceViaViewer: 'Μέσω της προσωπικής σύνδεσης κάθε ατόμου',
    manage: 'Διαχείριση',
    keptHeading: 'Διατηρημένο ιστορικό',
    keptSince: '{name} · από {since}',
    keptNote:
      'Διατηρείται μετά την εκκαθάριση όλων των καταχωρίσεων, εκτός αν ο κάτοχος επιλέξει το πλαίσιο.',
    pendingHeading: 'Αναμονή της έγκρισής σας',
    pendingNote:
      'Αυτές οι αλλαγές θα καταργήσουν δεδομένα. Δεν καταργείται τίποτα μέχρι να τις εγκρίνετε.',
    pendingReview: 'Έλεγχος',
    clearEntries: 'Εκκαθάριση όλων των καταχωρίσεων…',
    recovering: 'Ανάκτηση',
  },
  es: {
    tablesHeading: 'Tablas que conserva esta app',
    liveNote: 'Datos en tiempo real, iguales para todas las versiones',
    tableMeta: '{count} entradas · último cambio {at} por {who}',
    tableMetaNoChange: '{count} entradas',
    filesMeta: '{count} archivos · {size}',
    fields: '{count} campos',
    fieldsNoun: 'one:campo|other:campos',
    exportCsv: 'Exportar CSV',
    moreActions: 'Más acciones',
    viewFiles: 'Ver',
    restoreToTime: 'Restaurar a un momento…',
    rowHistory: 'Historial de la fila',
    rowHistoryTitle: 'Historial de esta fila',
    rowHistoryEmpty: 'No hay cambios registrados para esta fila.',
    rowHistoryCreated: 'Añadido por {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} cambió',
    rowHistoryBy: '{who}, {at}',
    undo: 'Deshacer',
    undoBusy: 'Deshaciendo…',
    undoUnavailable: 'No se puede deshacer',
    absent: '—',
    loadMore: 'Mostrar más',
    loading: 'Cargando entradas…',
    loadFailed: 'No se pudieron cargar las entradas. Vuelve a intentarlo.',
    rowsFailed: 'No se pudieron cargar las entradas.',
    retry: 'Volver a intentarlo',
    noEntries: 'Aún no hay entradas.',
    searchLabel: 'Buscar entradas',
    sourcesHeading: 'Fuentes que consulta',
    sourcesNone: 'No consulta sistemas de la empresa. Sus datos son propios.',
    sourceReadOnly: 'Solo lectura',
    sourceReadWrite: 'Lee y propone cambios',
    sourceAllowed: 'Permitido por {who}, {at}',
    sourceLastRead: 'Última consulta {at}',
    sourceNeverRead: 'Aún no se ha leído',
    sourceViaOwner: 'Mediante la conexión de {owner}',
    sourceViaViewer: 'Mediante la conexión propia de cada persona',
    manage: 'Gestionar',
    keptHeading: 'Historial conservado',
    keptSince: '{name} · desde {since}',
    keptNote:
      'Se conserva aunque se borren todas las entradas, a menos que el propietario marque la casilla.',
    pendingHeading: 'Esperando tu aprobación',
    pendingNote: 'Estos cambios eliminarían datos. No se elimina nada hasta que los apruebes.',
    pendingReview: 'Revisar',
    clearEntries: 'Borrar todas las entradas…',
    recovering: 'Recuperando',
  },
  et: {
    tablesHeading: 'Tabelid, mida see rakendus haldab',
    liveNote: 'Reaalajas andmed, kõigi versioonide puhul samad',
    tableMeta: '{count} kirjet · viimane muudatus {at}, tegija {who}',
    tableMetaNoChange: '{count} kirjet',
    filesMeta: '{count} faili · {size}',
    fields: '{count} välja',
    fieldsNoun: 'one:väli|other:väljad',
    exportCsv: 'Ekspordi CSV',
    moreActions: 'Veel toiminguid',
    viewFiles: 'Vaata',
    restoreToTime: 'Taasta ajahetke seis…',
    rowHistory: 'Rea ajalugu',
    rowHistoryTitle: 'Selle rea ajalugu',
    rowHistoryEmpty: 'Selle rea kohta pole muudatusi salvestatud.',
    rowHistoryCreated: 'Lisas {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} muutus',
    rowHistoryBy: '{who}, {at}',
    undo: 'Võta tagasi',
    undoBusy: 'Tagasivõtmine…',
    undoUnavailable: 'Ei saa tagasi võtta',
    absent: '—',
    loadMore: 'Näita rohkem',
    loading: 'Kirjete laadimine…',
    loadFailed: 'Kirjeid ei õnnestunud laadida. Proovi uuesti.',
    rowsFailed: 'Kirjeid ei õnnestunud laadida.',
    retry: 'Proovi uuesti',
    noEntries: 'Kirjeid veel pole.',
    searchLabel: 'Otsi kirjeid',
    sourcesHeading: 'Allikad, millest see loeb',
    sourcesNone: 'See ei loe andmeid ühestki ettevõtte süsteemist. Selle andmed on tema enda omad.',
    sourceReadOnly: 'Ainult lugemine',
    sourceReadWrite: 'Loeb ja teeb kirjutamisettepanekuid',
    sourceAllowed: 'Lubas {who}, {at}',
    sourceLastRead: 'Viimati loetud {at}',
    sourceNeverRead: 'Veel lugemata',
    sourceViaOwner: '{owner} ühenduse kaudu',
    sourceViaViewer: 'Iga inimese enda ühenduse kaudu',
    manage: 'Halda',
    keptHeading: 'Säilitatud ajalugu',
    keptSince: '{name} · alates {since}',
    keptNote:
      'Säilib pärast kõigi kirjete kustutamist, välja arvatud juhul, kui omanik märgib kasti.',
    pendingHeading: 'Ootab sinu kinnitust',
    pendingNote: 'Need muudatused eemaldaksid andmeid. Midagi ei eemaldata enne sinu kinnitust.',
    pendingReview: 'Vaata üle',
    clearEntries: 'Kustuta kõik kirjed…',
    recovering: 'Taastamine',
  },
  fi: {
    tablesHeading: 'Tämän sovelluksen ylläpitämät taulukot',
    liveNote: 'Ajantasaiset tiedot, samat kaikissa versioissa',
    tableMeta: '{count} merkintää · viimeisin muutos {at}, tekijä {who}',
    tableMetaNoChange: '{count} merkintää',
    filesMeta: '{count} tiedostoa · {size}',
    fields: '{count} kenttää',
    fieldsNoun: 'one:kenttä|other:kenttää',
    exportCsv: 'Vie CSV-tiedostona',
    moreActions: 'Lisää toimintoja',
    viewFiles: 'Näytä',
    restoreToTime: 'Palauta ajankohtaan…',
    rowHistory: 'Rivin historia',
    rowHistoryTitle: 'Tämän rivin historia',
    rowHistoryEmpty: 'Tästä rivistä ei ole tallennettu muutoksia.',
    rowHistoryCreated: 'Lisännyt {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} muuttui',
    rowHistoryBy: '{who}, {at}',
    undo: 'Kumoa',
    undoBusy: 'Kumotaan…',
    undoUnavailable: 'Ei voi kumota',
    absent: '—',
    loadMore: 'Näytä lisää',
    loading: 'Ladataan merkintöjä…',
    loadFailed: 'Merkintöjä ei voitu ladata. Yritä uudelleen.',
    rowsFailed: 'Merkintöjä ei voitu ladata.',
    retry: 'Yritä uudelleen',
    noEntries: 'Ei vielä merkintöjä.',
    searchLabel: 'Hae merkintöjä',
    sourcesHeading: 'Lähteet, joista se lukee',
    sourcesNone: 'Se ei lue yrityksen järjestelmiä. Sen tiedot ovat sen omia.',
    sourceReadOnly: 'Vain luku',
    sourceReadWrite: 'Lukee ja ehdottaa kirjoituksia',
    sourceAllowed: 'Sallinut {who}, {at}',
    sourceLastRead: 'Viimeksi luettu {at}',
    sourceNeverRead: 'Ei vielä luettu',
    sourceViaOwner: 'Käyttäjän {owner} yhteyden kautta',
    sourceViaViewer: 'Kunkin henkilön oman yhteyden kautta',
    manage: 'Hallinnoi',
    keptHeading: 'Säilytetty historia',
    keptSince: '{name} · alkaen {since}',
    keptNote: 'Säilyy kaikkien merkintöjen tyhjentämisen jälkeen, ellei omistaja valitse ruutua.',
    pendingHeading: 'Odottaa hyväksyntääsi',
    pendingNote:
      'Nämä muutokset poistaisivat tietoja. Mitään ei poisteta, ennen kuin hyväksyt sen.',
    pendingReview: 'Tarkista',
    clearEntries: 'Tyhjennä kaikki merkinnät…',
    recovering: 'Palautetaan',
  },
  fr: {
    tablesHeading: 'Tables conservées par cette app',
    liveNote: 'Données en temps réel, identiques pour toutes les versions',
    tableMeta: '{count} entrées · dernière modification {at} par {who}',
    tableMetaNoChange: '{count} entrées',
    filesMeta: '{count} fichiers · {size}',
    fields: '{count} champs',
    fieldsNoun: 'one:champ|other:champs',
    exportCsv: 'Exporter au format CSV',
    moreActions: 'Plus d’actions',
    viewFiles: 'Afficher',
    restoreToTime: 'Restaurer à une date…',
    rowHistory: 'Historique de la ligne',
    rowHistoryTitle: 'Historique de cette ligne',
    rowHistoryEmpty: 'Aucune modification enregistrée pour cette ligne.',
    rowHistoryCreated: 'Ajoutée par {who}',
    rowHistoryChanged: '{field} : {from} → {to}',
    rowHistoryImageChanged: '{field} a été modifié',
    rowHistoryBy: '{who}, {at}',
    undo: 'Annuler',
    undoBusy: 'Annulation…',
    undoUnavailable: 'Impossible d’annuler',
    absent: '—',
    loadMore: 'Afficher plus',
    loading: 'Chargement des entrées…',
    loadFailed: 'Impossible de charger les entrées. Réessayez.',
    rowsFailed: 'Impossible de charger les entrées.',
    retry: 'Réessayer',
    noEntries: 'Aucune entrée pour le moment.',
    searchLabel: 'Rechercher des entrées',
    sourcesHeading: 'Sources consultées',
    sourcesNone: 'Aucun système de l’entreprise n’est consulté. Ses données lui sont propres.',
    sourceReadOnly: 'Lecture seule',
    sourceReadWrite: 'Lecture et propositions de modifications',
    sourceAllowed: 'Autorisé par {who}, {at}',
    sourceLastRead: 'Dernière lecture {at}',
    sourceNeverRead: 'Pas encore lu',
    sourceViaOwner: 'Via la connexion de {owner}',
    sourceViaViewer: 'Via la connexion de chaque personne',
    manage: 'Gérer',
    keptHeading: 'Historique conservé',
    keptSince: '{name} · depuis {since}',
    keptNote:
      'Résiste à « Effacer toutes les entrées » tant que le propriétaire ne coche pas la case.',
    pendingHeading: 'En attente de votre accord',
    pendingNote:
      'Ces modifications supprimeraient des données. Rien n’est supprimé avant votre approbation.',
    pendingReview: 'Examiner',
    clearEntries: 'Effacer toutes les entrées…',
    recovering: 'Récupération',
  },
  ga: {
    tablesHeading: 'Táblaí a choinníonn an aip seo',
    liveNote: 'Sonraí beo, mar an gcéanna i ngach leagan',
    tableMeta: '{count} iontráil · an t-athrú deireanach {at} ag {who}',
    tableMetaNoChange: '{count} iontráil',
    filesMeta: '{count} comhad · {size}',
    fields: '{count} réimse',
    fieldsNoun: 'one:réimse|other:réimsí',
    exportCsv: 'Easpórtáil CSV',
    moreActions: 'Tuilleadh gníomhartha',
    viewFiles: 'Féach',
    restoreToTime: 'Athchóirigh go ham…',
    rowHistory: 'Stair na róimhe',
    rowHistoryTitle: 'Stair na róimhe seo',
    rowHistoryEmpty: 'Níor taifeadadh aon athrú don ró seo.',
    rowHistoryCreated: 'Curtha leis ag {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: 'Athraíodh {field}',
    rowHistoryBy: '{who}, {at}',
    undo: 'Cealaigh',
    undoBusy: 'Á chealú…',
    undoUnavailable: 'Ní féidir é a chealú',
    absent: '—',
    loadMore: 'Taispeáin tuilleadh',
    loading: 'Iontrálacha á lódáil…',
    loadFailed: 'Níorbh fhéidir na hiontrálacha a lódáil. Bain triail eile as.',
    rowsFailed: 'Níorbh fhéidir na hiontrálacha a lódáil.',
    retry: 'Bain triail eile as',
    noEntries: 'Níl aon iontráil ann fós.',
    searchLabel: 'Cuardaigh iontrálacha',
    sourcesHeading: 'Foinsí a léann sé',
    sourcesNone: 'Ní léann sé córais ar bith de chuid na cuideachta. Is leis féin a chuid sonraí.',
    sourceReadOnly: 'Inléite amháin',
    sourceReadWrite: 'Léann sé agus molann sé scríobh',
    sourceAllowed: 'Ceadaithe ag {who}, {at}',
    sourceLastRead: 'Léadh an uair dheireanach {at}',
    sourceNeverRead: 'Níl léite fós',
    sourceViaOwner: 'Trí nasc {owner}',
    sourceViaViewer: 'Trí nasc gach duine féin',
    manage: 'Bainistigh',
    keptHeading: 'Stair coinnithe',
    keptSince: '{name} · ó {since}',
    keptNote:
      'Fanann sé seo tar éis Gach iontráil a ghlanadh, mura gcuireann an t-úinéir tic sa bhosca.',
    pendingHeading: 'Ag fanacht le do cheadú',
    pendingNote:
      'Bainfeadh na hathruithe seo sonraí. Ní bhaintear aon rud go dtí go gceadaíonn tú iad.',
    pendingReview: 'Athbhreithnigh',
    clearEntries: 'Glan gach iontráil…',
    recovering: 'Á n-aisghabháil',
  },
  hr: {
    tablesHeading: 'Tablice koje ova aplikacija održava',
    liveNote: 'Aktivni podaci, isti za svaku verziju',
    tableMeta: '{count} zapisa · posljednja promjena {at}, korisnik {who}',
    tableMetaNoChange: '{count} zapisa',
    filesMeta: '{count} datoteka · {size}',
    fields: '{count} polja',
    fieldsNoun: 'one:polje|other:polja',
    exportCsv: 'Izvezi CSV',
    moreActions: 'Više radnji',
    viewFiles: 'Prikaži',
    restoreToTime: 'Vrati na vrijeme…',
    rowHistory: 'Povijest retka',
    rowHistoryTitle: 'Povijest ovog retka',
    rowHistoryEmpty: 'Za ovaj redak nisu zabilježene promjene.',
    rowHistoryCreated: 'Dodao/la {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} promijenjeno',
    rowHistoryBy: '{who}, {at}',
    undo: 'Poništi',
    undoBusy: 'Poništavanje…',
    undoUnavailable: 'Nije moguće poništiti',
    absent: '—',
    loadMore: 'Prikaži više',
    loading: 'Učitavanje zapisa…',
    loadFailed: 'Zapise nije moguće učitati. Pokušajte ponovno.',
    rowsFailed: 'Zapise nije moguće učitati.',
    retry: 'Pokušaj ponovno',
    noEntries: 'Još nema zapisa.',
    searchLabel: 'Pretraži zapise',
    sourcesHeading: 'Izvori koje čita',
    sourcesNone: 'Ne čita podatke iz sustava tvrtke. Njegovi su podaci vlastiti.',
    sourceReadOnly: 'Samo za čitanje',
    sourceReadWrite: 'Čita i predlaže upise',
    sourceAllowed: 'Dopustio/la {who}, {at}',
    sourceLastRead: 'Posljednje čitanje {at}',
    sourceNeverRead: 'Još nije pročitano',
    sourceViaOwner: 'Putem veze korisnika {owner}',
    sourceViaViewer: 'Putem vlastite veze svake osobe',
    manage: 'Upravljaj',
    keptHeading: 'Sačuvana povijest',
    keptSince: '{name} · od {since}',
    keptNote: 'Ostaje sačuvano nakon brisanja svih unosa, osim ako vlasnik ne označi kućicu.',
    pendingHeading: 'Čeka vaše odobrenje',
    pendingNote: 'Ove bi promjene uklonile podatke. Ništa se neće ukloniti dok ih ne odobrite.',
    pendingReview: 'Pregledaj',
    clearEntries: 'Izbriši sve unose…',
    recovering: 'Obnavljanje',
  },
  hu: {
    tablesHeading: 'Az app által kezelt táblák',
    liveNote: 'Élő adatok, minden verzióban azonosak',
    tableMeta: '{count} bejegyzés · utolsó módosítás: {at}, {who}',
    tableMetaNoChange: '{count} bejegyzés',
    filesMeta: '{count} fájl · {size}',
    fields: '{count} mező',
    fieldsNoun: 'one:mező|other:mezők',
    exportCsv: 'CSV exportálása',
    moreActions: 'További műveletek',
    viewFiles: 'Megtekintés',
    restoreToTime: 'Visszaállítás egy időpontra…',
    rowHistory: 'Sor előzményei',
    rowHistoryTitle: 'A sor előzményei',
    rowHistoryEmpty: 'Ehhez a sorhoz nincs rögzített módosítás.',
    rowHistoryCreated: 'Hozzáadta: {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: 'Módosult: {field}',
    rowHistoryBy: '{who}, {at}',
    undo: 'Visszavonás',
    undoBusy: 'Visszavonás…',
    undoUnavailable: 'Nem vonható vissza',
    absent: '—',
    loadMore: 'Továbbiak megjelenítése',
    loading: 'Bejegyzések betöltése…',
    loadFailed: 'A bejegyzéseket nem sikerült betölteni. Próbáld újra.',
    rowsFailed: 'A bejegyzéseket nem sikerült betölteni.',
    retry: 'Újra',
    noEntries: 'Még nincsenek bejegyzések.',
    searchLabel: 'Bejegyzések keresése',
    sourcesHeading: 'Beolvasott források',
    sourcesNone: 'Nem olvas vállalati rendszerekből. Az adatai saját adatok.',
    sourceReadOnly: 'Csak olvasás',
    sourceReadWrite: 'Olvas és módosításokat javasol',
    sourceAllowed: 'Engedélyezte: {who}, {at}',
    sourceLastRead: 'Utoljára olvasva: {at}',
    sourceNeverRead: 'Még nem olvasták el',
    sourceViaOwner: '{owner} kapcsolatán keresztül',
    sourceViaViewer: 'Mindenki a saját kapcsolatán keresztül',
    manage: 'Kezelés',
    keptHeading: 'Megőrzött előzmények',
    keptSince: '{name} · ettől: {since}',
    keptNote:
      'Túléli az összes bejegyzés törlését, kivéve, ha a tulajdonos bejelöli a jelölőnégyzetet.',
    pendingHeading: 'Jóváhagyásodra vár',
    pendingNote: 'Ezek a módosítások adatokat távolítanának el. Jóváhagyásodig semmi sem törlődik.',
    pendingReview: 'Áttekintés',
    clearEntries: 'Összes bejegyzés törlése…',
    recovering: 'Helyreállítás',
  },
  it: {
    tablesHeading: 'Tabelle gestite da questa app',
    liveNote: 'Dati in tempo reale, uguali per ogni versione',
    tableMeta: '{count} voci · ultima modifica {at} da {who}',
    tableMetaNoChange: '{count} voci',
    filesMeta: '{count} file · {size}',
    fields: '{count} campi',
    fieldsNoun: 'one:campo|other:campi',
    exportCsv: 'Esporta CSV',
    moreActions: 'Altre azioni',
    viewFiles: 'Visualizza',
    restoreToTime: 'Ripristina a un momento…',
    rowHistory: 'Cronologia della riga',
    rowHistoryTitle: 'Cronologia di questa riga',
    rowHistoryEmpty: 'Nessuna modifica registrata per questa riga.',
    rowHistoryCreated: 'Aggiunta da {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} modificato',
    rowHistoryBy: '{who}, {at}',
    undo: 'Annulla',
    undoBusy: 'Annullamento…',
    undoUnavailable: 'Non può essere annullato',
    absent: '—',
    loadMore: 'Mostra altro',
    loading: 'Caricamento delle voci…',
    loadFailed: 'Non è stato possibile caricare le voci. Riprova.',
    rowsFailed: 'Non è stato possibile caricare le voci.',
    retry: 'Riprova',
    noEntries: 'Ancora nessuna voce.',
    searchLabel: 'Cerca voci',
    sourcesHeading: 'Fonti consultate',
    sourcesNone: 'Non legge alcun sistema aziendale. I suoi dati sono autonomi.',
    sourceReadOnly: 'Sola lettura',
    sourceReadWrite: 'Legge e propone modifiche',
    sourceAllowed: 'Consentito da {who}, {at}',
    sourceLastRead: 'Ultima lettura {at}',
    sourceNeverRead: 'Non ancora letto',
    sourceViaOwner: 'Tramite la connessione di {owner}',
    sourceViaViewer: 'Tramite la connessione personale di ogni persona',
    manage: 'Gestisci',
    keptHeading: 'Cronologia conservata',
    keptSince: '{name} · dal {since}',
    keptNote:
      'Sopravvive a «Cancella tutte le voci» finché il proprietario non seleziona la casella.',
    pendingHeading: 'In attesa della tua conferma',
    pendingNote:
      'Queste modifiche rimuoverebbero dei dati. Nulla viene rimosso finché non approvi.',
    pendingReview: 'Esamina',
    clearEntries: 'Cancella tutte le voci…',
    recovering: 'Recupero in corso',
  },
  lt: {
    tablesHeading: 'Lentelės, kurias saugo ši programa',
    liveNote: 'Tiesioginiai duomenys, vienodi visoms versijoms',
    tableMeta: '{count} įrašų · paskutinį kartą pakeista {at}, pakeitė {who}',
    tableMetaNoChange: '{count} įrašų',
    filesMeta: '{count} failų · {size}',
    fields: '{count} laukų',
    fieldsNoun: 'one:lauką|other:laukų',
    exportCsv: 'Eksportuoti CSV',
    moreActions: 'Daugiau veiksmų',
    viewFiles: 'Peržiūrėti',
    restoreToTime: 'Atkurti pagal laiką…',
    rowHistory: 'Eilutės istorija',
    rowHistoryTitle: 'Šios eilutės istorija',
    rowHistoryEmpty: 'Šios eilutės pakeitimai neužregistruoti.',
    rowHistoryCreated: 'Pridėjo {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} pakeistas',
    rowHistoryBy: '{who}, {at}',
    undo: 'Anuliuoti',
    undoBusy: 'Anuliuojama…',
    undoUnavailable: 'Anuliuoti negalima',
    absent: '—',
    loadMore: 'Rodyti daugiau',
    loading: 'Įrašai įkeliami…',
    loadFailed: 'Įrašų nepavyko įkelti. Bandykite dar kartą.',
    rowsFailed: 'Įrašų nepavyko įkelti.',
    retry: 'Bandyti dar kartą',
    noEntries: 'Įrašų dar nėra.',
    searchLabel: 'Ieškoti įrašų',
    sourcesHeading: 'Šaltiniai, iš kurių skaito',
    sourcesNone: 'Neskaito iš įmonės sistemų. Jos duomenys yra nuosavi.',
    sourceReadOnly: 'Tik skaityti',
    sourceReadWrite: 'Skaito ir siūlo įrašyti pakeitimus',
    sourceAllowed: 'Leido {who}, {at}',
    sourceLastRead: 'Paskutinį kartą skaityta {at}',
    sourceNeverRead: 'Dar neskaityta',
    sourceViaOwner: 'Naudojant {owner} ryšį',
    sourceViaViewer: 'Naudojant kiekvieno asmens ryšį',
    manage: 'Tvarkyti',
    keptHeading: 'Išsaugota istorija',
    keptSince: '{name} · nuo {since}',
    keptNote: 'Išlieka išvalius visus įrašus, nebent savininkas pažymi langelį.',
    pendingHeading: 'Laukiama jūsų patvirtinimo',
    pendingNote: 'Šie pakeitimai pašalintų duomenis. Kol nepatvirtinsite, niekas nebus pašalinta.',
    pendingReview: 'Peržiūrėti',
    clearEntries: 'Išvalyti visus įrašus…',
    recovering: 'Atkuriama',
  },
  lv: {
    tablesHeading: 'Tabulas, ko uztur šī lietotne',
    liveNote: 'Aktuālie dati, vienādi visām versijām',
    tableMeta: '{count} ieraksti · pēdējās izmaiņas {at}, veica {who}',
    tableMetaNoChange: '{count} ieraksti',
    filesMeta: '{count} faili · {size}',
    fields: '{count} lauki',
    fieldsNoun: 'one:lauks|other:lauki',
    exportCsv: 'Eksportēt CSV',
    moreActions: 'Vairāk darbību',
    viewFiles: 'Skatīt',
    restoreToTime: 'Atjaunot uz laiku…',
    rowHistory: 'Rindas vēsture',
    rowHistoryTitle: 'Šīs rindas vēsture',
    rowHistoryEmpty: 'Šai rindai nav reģistrētu izmaiņu.',
    rowHistoryCreated: 'Pievienoja {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: 'Mainīts lauks {field}',
    rowHistoryBy: '{who}, {at}',
    undo: 'Atsaukt',
    undoBusy: 'Notiek atsaukšana…',
    undoUnavailable: 'Nevar atsaukt',
    absent: '—',
    loadMore: 'Rādīt vairāk',
    loading: 'Notiek ierakstu ielāde…',
    loadFailed: 'Ierakstus neizdevās ielādēt. Mēģiniet vēlreiz.',
    rowsFailed: 'Ierakstus neizdevās ielādēt.',
    retry: 'Mēģināt vēlreiz',
    noEntries: 'Vēl nav ierakstu.',
    searchLabel: 'Meklēt ierakstos',
    sourcesHeading: 'Avoti, no kuriem tā lasa',
    sourcesNone: 'Nolasa datus no uzņēmuma sistēmām. Tās dati ir pašas dati.',
    sourceReadOnly: 'Tikai lasāms',
    sourceReadWrite: 'Lasa un piedāvā veikt izmaiņas',
    sourceAllowed: 'Atļāva {who}, {at}',
    sourceLastRead: 'Pēdējo reizi lasīts {at}',
    sourceNeverRead: 'Vēl nav lasīts',
    sourceViaOwner: 'Izmantojot {owner} savienojumu',
    sourceViaViewer: 'Izmantojot katras personas savienojumu',
    manage: 'Pārvaldīt',
    keptHeading: 'Saglabātā vēsture',
    keptSince: '{name} · kopš {since}',
    keptNote:
      'Tā netiks dzēsta, izmantojot “Notīrīt visus ierakstus”, ja vien īpašnieks neatzīmēs izvēles rūtiņu.',
    pendingHeading: 'Gaida jūsu apstiprinājumu',
    pendingNote: 'Šīs izmaiņas dzēstu datus. Nekas netiks dzēsts, kamēr tās neapstiprināsiet.',
    pendingReview: 'Pārskatīt',
    clearEntries: 'Notīrīt visus ierakstus…',
    recovering: 'Atkopošana',
  },
  mt: {
    tablesHeading: 'Tabelli li żżomm din l-app',
    liveNote: 'Data diretta, l-istess għal kull verżjoni',
    tableMeta: '{count} entrati · l-aħħar bidla {at} minn {who}',
    tableMetaNoChange: '{count} entrati',
    filesMeta: '{count} fajls · {size}',
    fields: '{count} oqsma',
    fieldsNoun: 'one:qasam|other:oqsma',
    exportCsv: 'Esporta CSV',
    moreActions: 'Aktar azzjonijiet',
    viewFiles: 'Ara',
    restoreToTime: 'Irrestawra għal ħin…',
    rowHistory: 'Storja tar-ringiela',
    rowHistoryTitle: 'Storja ta’ din ir-ringiela',
    rowHistoryEmpty: 'Ma ġiet irreġistrata ebda bidla għal din ir-ringiela.',
    rowHistoryCreated: 'Miżjuda minn {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} inbidel',
    rowHistoryBy: '{who}, {at}',
    undo: 'Ħoll',
    undoBusy: 'Qed inħoll…',
    undoUnavailable: 'Ma jistax jitneħħa',
    absent: '—',
    loadMore: 'Uri aktar',
    loading: 'Qed jitgħabbew l-entrati…',
    loadFailed: 'L-entrati ma setgħux jitgħabbew. Erġa’ pprova.',
    rowsFailed: 'L-entrati ma setgħux jitgħabbew.',
    retry: 'Erġa’ pprova',
    noEntries: 'Għad m’hemmx entrati.',
    searchLabel: 'Fittex fl-entrati',
    sourcesHeading: 'Sorsi li taqra minnhom',
    sourcesNone: 'Ma taqra ebda sistema tal-kumpanija. Id-data tagħha hija tagħha stess.',
    sourceReadOnly: 'Aqra biss',
    sourceReadWrite: 'Taqra u tipproponi kitbiet',
    sourceAllowed: 'Permess minn {who}, {at}',
    sourceLastRead: 'L-aħħar qari {at}',
    sourceNeverRead: 'Għadu ma nqara xejn',
    sourceViaOwner: 'Permezz tal-konnessjoni ta’ {owner}',
    sourceViaViewer: 'Permezz tal-konnessjoni personali ta’ kull persuna',
    manage: 'Immaniġġja',
    keptHeading: 'Storja miżmuma',
    keptSince: '{name} · minn {since}',
    keptNote: 'Tibqa’ hemm wara Ħassar l-entrati kollha sakemm is-sid ma jimmarkax il-kaxxa.',
    pendingHeading: 'Qed tistenna l-approvazzjoni tiegħek',
    pendingNote: 'Dawn il-bidliet ineħħu d-dejta. Ma jitneħħa xejn sakemm tapprovahom.',
    pendingReview: 'Irrevedi',
    clearEntries: 'Ħassar l-entrati kollha…',
    recovering: 'Qed tiġi rkuprata',
  },
  nl: {
    tablesHeading: 'Tabellen die deze app bijhoudt',
    liveNote: 'Livegegevens, hetzelfde voor elke versie',
    tableMeta: '{count} vermeldingen · laatste wijziging {at} door {who}',
    tableMetaNoChange: '{count} vermeldingen',
    filesMeta: '{count} bestanden · {size}',
    fields: '{count} velden',
    fieldsNoun: 'one:veld|other:velden',
    exportCsv: 'CSV exporteren',
    moreActions: 'Meer acties',
    viewFiles: 'Bekijken',
    restoreToTime: 'Herstellen naar een tijdstip…',
    rowHistory: 'Rijgeschiedenis',
    rowHistoryTitle: 'Geschiedenis van deze rij',
    rowHistoryEmpty: 'Voor deze rij zijn geen wijzigingen vastgelegd.',
    rowHistoryCreated: 'Toegevoegd door {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} gewijzigd',
    rowHistoryBy: '{who}, {at}',
    undo: 'Ongedaan maken',
    undoBusy: 'Ongedaan maken…',
    undoUnavailable: 'Kan niet ongedaan worden gemaakt',
    absent: '—',
    loadMore: 'Meer tonen',
    loading: 'Vermeldingen laden…',
    loadFailed: 'De vermeldingen konden niet worden geladen. Probeer het opnieuw.',
    rowsFailed: 'De vermeldingen konden niet worden geladen.',
    retry: 'Opnieuw proberen',
    noEntries: 'Nog geen vermeldingen.',
    searchLabel: 'Vermeldingen zoeken',
    sourcesHeading: 'Bronnen die de app uitleest',
    sourcesNone: 'Leest geen bedrijfssystemen uit. De gegevens zijn van de app zelf.',
    sourceReadOnly: 'Alleen lezen',
    sourceReadWrite: 'Leest gegevens en stelt wijzigingen voor',
    sourceAllowed: 'Toegestaan door {who}, {at}',
    sourceLastRead: 'Laatst uitgelezen {at}',
    sourceNeverRead: 'Nog niet gelezen',
    sourceViaOwner: 'Via de verbinding van {owner}',
    sourceViaViewer: 'Via de eigen verbinding van elke persoon',
    manage: 'Beheren',
    keptHeading: 'Bewaarde geschiedenis',
    keptSince: '{name} · sinds {since}',
    keptNote:
      'Blijft behouden na ‘Alle vermeldingen wissen’, tenzij de eigenaar het vakje aanvinkt.',
    pendingHeading: 'Wachten op je OK',
    pendingNote:
      'Deze wijzigingen zouden gegevens verwijderen. Er wordt niets verwijderd totdat je goedkeuring geeft.',
    pendingReview: 'Controleren',
    clearEntries: 'Alle vermeldingen wissen…',
    recovering: 'Herstellen',
  },
  pl: {
    tablesHeading: 'Tabele przechowywane przez tę aplikację',
    liveNote: 'Dane na żywo, takie same dla każdej wersji',
    tableMeta: '{count} wpisów · ostatnia zmiana: {at}, autor: {who}',
    tableMetaNoChange: '{count} wpisów',
    filesMeta: '{count} plików · {size}',
    fields: '{count} pól',
    fieldsNoun: 'one:pole|other:pól',
    exportCsv: 'Eksportuj CSV',
    moreActions: 'Więcej działań',
    viewFiles: 'Wyświetl',
    restoreToTime: 'Przywróć stan z wybranego momentu…',
    rowHistory: 'Historia wiersza',
    rowHistoryTitle: 'Historia tego wiersza',
    rowHistoryEmpty: 'Nie zarejestrowano zmian dla tego wiersza.',
    rowHistoryCreated: 'Dodano przez {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: 'Zmieniono {field}',
    rowHistoryBy: '{who}, {at}',
    undo: 'Cofnij',
    undoBusy: 'Cofanie…',
    undoUnavailable: 'Nie można cofnąć',
    absent: '—',
    loadMore: 'Pokaż więcej',
    loading: 'Ładowanie wpisów…',
    loadFailed: 'Nie udało się załadować wpisów. Spróbuj ponownie.',
    rowsFailed: 'Nie udało się załadować wpisów.',
    retry: 'Spróbuj ponownie',
    noEntries: 'Brak wpisów.',
    searchLabel: 'Szukaj wpisów',
    sourcesHeading: 'Źródła, z których odczytuje dane',
    sourcesNone: 'Nie odczytuje danych z systemów firmowych. Jej dane są własne.',
    sourceReadOnly: 'Tylko do odczytu',
    sourceReadWrite: 'Odczytuje dane i proponuje ich zapis',
    sourceAllowed: 'Zezwolono przez {who}, {at}',
    sourceLastRead: 'Ostatni odczyt: {at}',
    sourceNeverRead: 'Jeszcze nieodczytane',
    sourceViaOwner: 'Przez połączenie {owner}’a',
    sourceViaViewer: 'Przez własne połączenie każdej osoby',
    manage: 'Zarządzaj',
    keptHeading: 'Zachowana historia',
    keptSince: '{name} · od {since}',
    keptNote: 'Przetrwa opcję „Wyczyść wszystkie wpisy”, chyba że właściciel zaznaczy to pole.',
    pendingHeading: 'Oczekuje na Twoją zgodę',
    pendingNote:
      'Te zmiany usunęłyby dane. Nic nie zostanie usunięte, dopóki ich nie zatwierdzisz.',
    pendingReview: 'Sprawdź',
    clearEntries: 'Wyczyść wszystkie wpisy…',
    recovering: 'Odzyskiwanie',
  },
  pt: {
    tablesHeading: 'Tabelas mantidas por este app',
    liveNote: 'Dados em tempo real, iguais em todas as versões',
    tableMeta: '{count} entradas · última alteração {at} por {who}',
    tableMetaNoChange: '{count} entradas',
    filesMeta: '{count} arquivos · {size}',
    fields: '{count} campos',
    fieldsNoun: 'one:campo|other:campos',
    exportCsv: 'Exportar CSV',
    moreActions: 'Mais ações',
    viewFiles: 'Ver',
    restoreToTime: 'Restaurar para um horário…',
    rowHistory: 'Histórico da linha',
    rowHistoryTitle: 'Histórico desta linha',
    rowHistoryEmpty: 'Nenhuma alteração registrada para esta linha.',
    rowHistoryCreated: 'Adicionado por {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} alterado',
    rowHistoryBy: '{who}, {at}',
    undo: 'Desfazer',
    undoBusy: 'Desfazendo…',
    undoUnavailable: 'Não é possível desfazer',
    absent: '—',
    loadMore: 'Mostrar mais',
    loading: 'Carregando entradas…',
    loadFailed: 'Não foi possível carregar as entradas. Tente novamente.',
    rowsFailed: 'Não foi possível carregar as entradas.',
    retry: 'Tentar novamente',
    noEntries: 'Ainda não há entradas.',
    searchLabel: 'Pesquisar entradas',
    sourcesHeading: 'Fontes que ele consulta',
    sourcesNone: 'Não consulta sistemas da empresa. Os dados são próprios.',
    sourceReadOnly: 'Somente leitura',
    sourceReadWrite: 'Lê e propõe gravações',
    sourceAllowed: 'Permitido por {who}, {at}',
    sourceLastRead: 'Última leitura {at}',
    sourceNeverRead: 'Ainda não lido',
    sourceViaOwner: 'Através da conexão de {owner}',
    sourceViaViewer: 'Através da conexão de cada pessoa',
    manage: 'Gerenciar',
    keptHeading: 'Histórico mantido',
    keptSince: '{name} · desde {since}',
    keptNote: 'Permanece após Limpar todas as entradas, a menos que o proprietário marque a caixa.',
    pendingHeading: 'Aguardando sua aprovação',
    pendingNote: 'Estas alterações removeriam dados. Nada será removido até você aprovar.',
    pendingReview: 'Revisar',
    clearEntries: 'Limpar todas as entradas…',
    recovering: 'Recuperando',
  },
  ro: {
    tablesHeading: 'Tabelele păstrate de această aplicație',
    liveNote: 'Date live, identice pentru fiecare versiune',
    tableMeta: '{count} înregistrări · ultima modificare {at} de {who}',
    tableMetaNoChange: '{count} înregistrări',
    filesMeta: '{count} fișiere · {size}',
    fields: '{count} câmpuri',
    fieldsNoun: 'one:câmp|other:câmpuri',
    exportCsv: 'Exportă CSV',
    moreActions: 'Mai multe acțiuni',
    viewFiles: 'Vezi',
    restoreToTime: 'Restaurează la un moment…',
    rowHistory: 'Istoricul rândului',
    rowHistoryTitle: 'Istoricul acestui rând',
    rowHistoryEmpty: 'Nu există modificări înregistrate pentru acest rând.',
    rowHistoryCreated: 'Adăugat de {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} a fost modificat',
    rowHistoryBy: '{who}, {at}',
    undo: 'Anulează',
    undoBusy: 'Se anulează…',
    undoUnavailable: 'Nu se poate anula',
    absent: '—',
    loadMore: 'Afișează mai multe',
    loading: 'Se încarcă înregistrările…',
    loadFailed: 'Înregistrările nu au putut fi încărcate. Încercați din nou.',
    rowsFailed: 'Înregistrările nu au putut fi încărcate.',
    retry: 'Încearcă din nou',
    noEntries: 'Nu există încă înregistrări.',
    searchLabel: 'Caută înregistrări',
    sourcesHeading: 'Sursele pe care le citește',
    sourcesNone: 'Nu citește din sistemele companiei. Datele îi aparțin.',
    sourceReadOnly: 'Doar citire',
    sourceReadWrite: 'Citește și propune modificări',
    sourceAllowed: 'Permis de {who}, {at}',
    sourceLastRead: 'Ultima citire: {at}',
    sourceNeverRead: 'Nu a fost citit încă',
    sourceViaOwner: 'Prin conexiunea lui {owner}',
    sourceViaViewer: 'Prin conexiunea proprie a fiecărei persoane',
    manage: 'Gestionează',
    keptHeading: 'Istoric păstrat',
    keptSince: '{name} · din {since}',
    keptNote:
      'Rămâne după „Șterge toate înregistrările”, cu excepția cazului în care proprietarul bifează caseta.',
    pendingHeading: 'Așteaptă acordul tău',
    pendingNote: 'Aceste modificări ar elimina date. Nimic nu este eliminat până nu le aprobi.',
    pendingReview: 'Examinează',
    clearEntries: 'Șterge toate înregistrările…',
    recovering: 'Recuperare',
  },
  sk: {
    tablesHeading: 'Tabuľky, ktoré táto aplikácia uchováva',
    liveNote: 'Živé údaje, rovnaké pre každú verziu',
    tableMeta: '{count} záznamov · posledná zmena {at} používateľom {who}',
    tableMetaNoChange: '{count} záznamov',
    filesMeta: '{count} súborov · {size}',
    fields: '{count} polí',
    fieldsNoun: 'one:pole|other:polia',
    exportCsv: 'Exportovať CSV',
    moreActions: 'Ďalšie akcie',
    viewFiles: 'Zobraziť',
    restoreToTime: 'Obnoviť stav k času…',
    rowHistory: 'História riadka',
    rowHistoryTitle: 'História tohto riadka',
    rowHistoryEmpty: 'Pre tento riadok nie sú zaznamenané žiadne zmeny.',
    rowHistoryCreated: 'Pridal(a) {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} sa zmenilo',
    rowHistoryBy: '{who}, {at}',
    undo: 'Späť',
    undoBusy: 'Vracanie zmeny…',
    undoUnavailable: 'Nemožno vrátiť späť',
    absent: '—',
    loadMore: 'Zobraziť viac',
    loading: 'Načítavajú sa záznamy…',
    loadFailed: 'Záznamy sa nepodarilo načítať. Skúste to znova.',
    rowsFailed: 'Záznamy sa nepodarilo načítať.',
    retry: 'Skúsiť znova',
    noEntries: 'Zatiaľ žiadne záznamy.',
    searchLabel: 'Hľadať v záznamoch',
    sourcesHeading: 'Zdroje, z ktorých číta',
    sourcesNone: 'Nečíta zo žiadnych firemných systémov. Jeho údaje sú vlastné.',
    sourceReadOnly: 'Iba na čítanie',
    sourceReadWrite: 'Číta a navrhuje zápisy',
    sourceAllowed: 'Povolil(a) {who}, {at}',
    sourceLastRead: 'Naposledy čítané {at}',
    sourceNeverRead: 'Zatiaľ neprečítané',
    sourceViaOwner: 'Prostredníctvom pripojenia používateľa {owner}',
    sourceViaViewer: 'Prostredníctvom vlastného pripojenia každej osoby',
    manage: 'Spravovať',
    keptHeading: 'Zachovaná história',
    keptSince: '{name} · od {since}',
    keptNote: 'Pretrvá aj po vymazaní všetkých záznamov, pokiaľ vlastník nezaškrtne políčko.',
    pendingHeading: 'Čaká na vaše schválenie',
    pendingNote: 'Tieto zmeny by odstránili údaje. Nič sa neodstráni, kým ich neschválite.',
    pendingReview: 'Skontrolovať',
    clearEntries: 'Vymazať všetky záznamy…',
    recovering: 'Obnovuje sa',
  },
  sl: {
    tablesHeading: 'Tabele, ki jih ta aplikacija vzdržuje',
    liveNote: 'Aktivni podatki, enaki za vse različice',
    tableMeta: '{count} vnosov · zadnja sprememba {at} uporabnika {who}',
    tableMetaNoChange: '{count} vnosov',
    filesMeta: '{count} datotek · {size}',
    fields: '{count} polj',
    fieldsNoun: 'one:polje|other:polja',
    exportCsv: 'Izvozi CSV',
    moreActions: 'Več dejanj',
    viewFiles: 'Ogled',
    restoreToTime: 'Obnovi na čas…',
    rowHistory: 'Zgodovina vrstice',
    rowHistoryTitle: 'Zgodovina te vrstice',
    rowHistoryEmpty: 'Za to vrstico ni zabeleženih sprememb.',
    rowHistoryCreated: 'Dodal/-a {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} je bil spremenjen',
    rowHistoryBy: '{who}, {at}',
    undo: 'Razveljavi',
    undoBusy: 'Razveljavljanje…',
    undoUnavailable: 'Ni mogoče razveljaviti',
    absent: '—',
    loadMore: 'Prikaži več',
    loading: 'Nalaganje vnosov…',
    loadFailed: 'Vnosov ni bilo mogoče naložiti. Poskusite znova.',
    rowsFailed: 'Vnosov ni bilo mogoče naložiti.',
    retry: 'Poskusi znova',
    noEntries: 'Vnosov še ni.',
    searchLabel: 'Iskanje vnosov',
    sourcesHeading: 'Viri, iz katerih bere',
    sourcesNone: 'Ne bere iz sistemov podjetja. Njeni podatki so lastni.',
    sourceReadOnly: 'Samo za branje',
    sourceReadWrite: 'Bere in predlaga zapise',
    sourceAllowed: 'Dovolil/-a {who}, {at}',
    sourceLastRead: 'Nazadnje prebrano {at}',
    sourceNeverRead: 'Še ni prebrano',
    sourceViaOwner: 'Prek povezave osebe {owner}',
    sourceViaViewer: 'Prek lastne povezave vsake osebe',
    manage: 'Upravljaj',
    keptHeading: 'Ohranjena zgodovina',
    keptSince: '{name} · od {since}',
    keptNote: 'Ostane tudi po možnosti »Počisti vse vnose«, razen če lastnik označi polje.',
    pendingHeading: 'Čaka na vašo odobritev',
    pendingNote: 'Te spremembe bi odstranile podatke. Nič se ne odstrani, dokler jih ne odobrite.',
    pendingReview: 'Preglej',
    clearEntries: 'Počisti vse vnose…',
    recovering: 'Obnavljanje',
  },
  sv: {
    tablesHeading: 'Tabeller som appen sparar',
    liveNote: 'Live-data, densamma för alla versioner',
    tableMeta: '{count} poster · senaste ändring {at} av {who}',
    tableMetaNoChange: '{count} poster',
    filesMeta: '{count} filer · {size}',
    fields: '{count} fält',
    fieldsNoun: 'one:fält|other:fält',
    exportCsv: 'Exportera CSV',
    moreActions: 'Fler åtgärder',
    viewFiles: 'Visa',
    restoreToTime: 'Återställ till en tidpunkt…',
    rowHistory: 'Radhistorik',
    rowHistoryTitle: 'Historik för den här raden',
    rowHistoryEmpty: 'Inga ändringar registrerade för den här raden.',
    rowHistoryCreated: 'Lades till av {who}',
    rowHistoryChanged: '{field}: {from} → {to}',
    rowHistoryImageChanged: '{field} ändrades',
    rowHistoryBy: '{who}, {at}',
    undo: 'Ångra',
    undoBusy: 'Ångrar…',
    undoUnavailable: 'Kan inte ångras',
    absent: '—',
    loadMore: 'Visa fler',
    loading: 'Laddar poster…',
    loadFailed: 'Posterna kunde inte laddas. Försök igen.',
    rowsFailed: 'Posterna kunde inte laddas.',
    retry: 'Försök igen',
    noEntries: 'Inga poster än.',
    searchLabel: 'Sök poster',
    sourcesHeading: 'Källor som appen läser',
    sourcesNone: 'Läser inga företagssystem. Dess data är dess egen.',
    sourceReadOnly: 'Endast läsning',
    sourceReadWrite: 'Läser och föreslår skrivningar',
    sourceAllowed: 'Tillåten av {who}, {at}',
    sourceLastRead: 'Senast läst {at}',
    sourceNeverRead: 'Inte läst än',
    sourceViaOwner: 'Via {owner}s anslutning',
    sourceViaViewer: 'Via var och ens egen anslutning',
    manage: 'Hantera',
    keptHeading: 'Sparad historik',
    keptSince: '{name} · sedan {since}',
    keptNote: 'Finns kvar när alla poster rensas, om inte ägaren kryssar i rutan.',
    pendingHeading: 'Väntar på ditt OK',
    pendingNote: 'De här ändringarna skulle ta bort data. Inget tas bort förrän du godkänner.',
    pendingReview: 'Granska',
    clearEntries: 'Rensa alla poster…',
    recovering: 'Återställer',
  },
};
export default appDataCopy;
